import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from './Icon.js';
import { supabase } from '../supabase.js';
import './ProtocolNotesSlideOver.css';

const ProtocolNotesSlideOver = ({
  isOpen,
  onClose,
  protocol,
  userId
}) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedDates, setExpandedDates] = useState(new Set()); // Track which dates are expanded
  const [editingNoteId, setEditingNoteId] = useState(null); // Track which note is being edited
  const [editingContent, setEditingContent] = useState(''); // Content for inline editing
  const [editingTitle, setEditingTitle] = useState(''); // Title for inline editing
  const [editingDate, setEditingDate] = useState(null); // Date for new note being created
  const [newNoteContent, setNewNoteContent] = useState(''); // Content for new note
  const [newNoteTitle, setNewNoteTitle] = useState(''); // Title for new note

  // Load notes when protocol changes
  useEffect(() => {
    const loadNotes = async () => {
      if (!protocol || !isOpen || !userId) {
        setNotes([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('protocol_notes')
          .select('*')
          .eq('user_id', userId)
          .eq('protocol_id', protocol.id)
          .order('note_date', { ascending: false });

        if (error) {
          console.error('Failed to load notes:', error);
          setNotes([]);
        } else {
          setNotes(data || []);
        }
      } catch (error) {
        console.error('Error loading notes:', error);
        setNotes([]);
      } finally {
        setLoading(false);
      }
    };

    loadNotes();
  }, [protocol, isOpen, userId]);

  // Initialize editing content when editing starts
  useEffect(() => {
    if (editingNoteId) {
      const note = notes.find(n => n.id === editingNoteId);
      if (note) {
        setEditingContent(note.content || '');
        setEditingTitle(note.title || '');
      }
    } else {
      setEditingContent('');
      setEditingTitle('');
    }
  }, [editingNoteId, notes]);

  // Toggle date expansion
  const toggleDateExpansion = (date) => {
    // Don't toggle if currently editing in this date group
    if (editingNoteId || editingDate === date) {
      const note = notes.find(n => n.id === editingNoteId);
      if (note && note.note_date === date) return;
    }
    
    setExpandedDates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  // Start editing a note
  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingContent(note.content);
    setEditingDate(null);
    setNewNoteContent('');
    // Expand the date if not already expanded
    setExpandedDates(prev => new Set(prev).add(note.note_date));
  };

  // Start creating a new note for a date
  const handleAddNote = (date) => {
    setEditingDate(date);
    setNewNoteContent('');
    setEditingNoteId(null);
    setEditingContent('');
    // Expand the date if not already expanded
    setExpandedDates(prev => new Set(prev).add(date));
  };

  // Cancel creating new note
  const handleCancelNewNote = () => {
    setEditingDate(null);
    setNewNoteContent('');
  };

  // Save new note
  const handleSaveNewNote = async () => {
    if (!protocol || !userId || !editingDate || !newNoteContent.trim()) return;

    setSaving(true);
    try {
      const noteData = {
        user_id: userId,
        protocol_id: protocol.id,
        note_date: editingDate,
        content: newNoteContent.trim(),
        title: newNoteTitle.trim() || null
      };

      const { data, error } = await supabase
        .from('protocol_notes')
        .insert([noteData])
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setNotes(prev => [data, ...prev]);
      setEditingDate(null);
      setNewNoteContent('');
      setNewNoteTitle('');
    } catch (error) {
      console.error('Failed to save note:', error);
      alert('Failed to save note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle inline editing save
  const handleSaveInlineEdit = async (noteId) => {
    if (!editingContent.trim()) return;

    setSaving(true);
    try {
      const updateData = {
        content: editingContent.trim(),
        title: editingTitle.trim() || null
      };

      const { error } = await supabase
        .from('protocol_notes')
        .update(updateData)
        .eq('id', noteId);

      if (error) throw error;

      // Update local state
      setNotes(prev => prev.map(n => 
        n.id === noteId 
          ? { ...n, content: editingContent.trim(), title: editingTitle.trim() || null }
          : n
      ));

      setEditingNoteId(null);
      setEditingContent('');
      setEditingTitle('');
    } catch (error) {
      console.error('Failed to save note:', error);
      alert('Failed to save note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Cancel inline editing
  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingContent('');
    setEditingTitle('');
  };

  // Group notes by date
  const notesByDate = useMemo(() => {
    const grouped = {};
    notes.forEach(note => {
      const date = note.note_date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(note);
    });
    return grouped;
  }, [notes]);

  // Get unique dates, sorted descending, including dates for new notes being created
  const dates = useMemo(() => {
    const allDates = new Set(Object.keys(notesByDate));
    if (editingDate && !allDates.has(editingDate)) {
      allDates.add(editingDate);
    }
    return Array.from(allDates).sort((a, b) => new Date(b) - new Date(a));
  }, [notesByDate, editingDate]);

  // Handle deleting note
  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      const { error } = await supabase
        .from('protocol_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      // Update local state
      setNotes(prev => prev.filter(n => n.id !== noteId));
      
      // Clear editing if deleted note was being edited
      if (editingNoteId === noteId) {
        setEditingNoteId(null);
        setEditingContent('');
      }
      
      // Close date expansion if no notes left for that date
      const deletedNote = notes.find(n => n.id === noteId);
      if (deletedNote) {
        const remainingNotesForDate = notes.filter(n => 
          n.id !== noteId && n.note_date === deletedNote.note_date
        );
        if (remainingNotesForDate.length === 0) {
          setExpandedDates(prev => {
            const newSet = new Set(prev);
            newSet.delete(deletedNote.note_date);
            return newSet;
          });
        }
      }
    } catch (error) {
      console.error('Failed to delete note:', error);
      alert('Failed to delete note. Please try again.');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  // Get time category for a note (morning, afternoon, evening)
  const getTimeCategory = (note) => {
    const createdTime = new Date(note.created_at || note.updated_at);
    const hour = createdTime.getHours();
    
    if (hour >= 5 && hour < 12) {
      return 'morning';
    } else if (hour >= 12 && hour < 17) {
      return 'afternoon';
    } else {
      return 'evening';
    }
  };

  // Group notes by time category within a date
  const groupNotesByCategory = (notes) => {
    const categories = {
      morning: [],
      afternoon: [],
      evening: []
    };
    
    notes.forEach(note => {
      const category = getTimeCategory(note);
      categories[category].push(note);
    });
    
    // Sort notes within each category by creation time (oldest first)
    Object.keys(categories).forEach(category => {
      categories[category].sort((a, b) => {
        const timeA = new Date(a.created_at || a.updated_at);
        const timeB = new Date(b.created_at || b.updated_at);
        return timeA - timeB;
      });
    });
    
    return categories;
  };

  // Category display names
  const categoryNames = {
    morning: 'Morning',
    afternoon: 'Afternoon',
    evening: 'Evening'
  };

  if (!isOpen || !protocol) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="protocol-notes-slide-over-backdrop"
        onClick={onClose}
      />

      {/* Slide-over panel */}
      <div className="protocol-notes-slide-over">
        {/* Mobile Handle */}
        <div 
          className="protocol-notes-mobile-handle"
          onClick={onClose}
        >
          <div className="mobile-handle-content">
            <div className="mobile-handle-text">
              <span className="mobile-handle-title">
                Notes
                <span className="mobile-handle-count">
                  ({notes.length})
                </span>
              </span>
              {protocol?.name && (
                <span className="mobile-handle-subtitle">{protocol.name}</span>
              )}
            </div>
            <Icon 
              name="ChevronDown" 
              className="icon--sm" 
            />
          </div>
        </div>

        {/* Header */}
        <div className="protocol-notes-slide-over-header">
          <div className="protocol-notes-slide-over-header-content">
            <div className="protocol-notes-slide-over-title-section">
              <h2 className="protocol-notes-slide-over-header-title">
                <span>Notes</span>
                <span className="protocol-notes-count-badge">
                  ({notes.length} note{notes.length !== 1 ? 's' : ''})
                </span>
              </h2>
              {protocol?.name && (
                <p className="protocol-notes-protocol-name">{protocol.name}</p>
              )}
            </div>
            <button
              className="protocol-notes-slide-over-close-btn"
              onClick={onClose}
              aria-label="Close notes panel"
            >
              <Icon name="X" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="protocol-notes-slide-over-body">
          {loading ? (
            <div className="protocol-notes-loading">
              <div className="protocol-notes-spinner"></div>
              <span>Loading notes...</span>
            </div>
          ) : (
            <div className="protocol-notes-content">
              {/* Notes history */}
              <div className="protocol-notes-history">
                <div className="protocol-notes-history-header">
                  <button
                    className="protocol-notes-add-note-top-btn"
                    onClick={() => {
                      const today = new Date().toISOString().split('T')[0];
                      handleAddNote(today);
                    }}
                  >
                    <Icon name="Plus" className="icon--sm" />
                    Add Note
                  </button>
                </div>
                {dates.length > 0 ? (
                  <div className="protocol-notes-dates-list">
                    {dates.map(date => {
                      const dateNotes = notesByDate[date] || [];
                      const isExpanded = expandedDates.has(date);
                      
                      return (
                        <div key={date} className="protocol-notes-date-group">
                          <div className="protocol-notes-date-header-wrapper">
                            <button
                              className="protocol-notes-date-header"
                              onClick={() => toggleDateExpansion(date)}
                            >
                              <div className="protocol-notes-date-header-left">
                                <Icon 
                                  name="ChevronDown" 
                                  className={`protocol-notes-chevron ${isExpanded ? 'expanded' : ''}`} 
                                />
                                <span className="protocol-notes-date-label-text">{formatDate(date)}</span>
                                <span className="protocol-notes-date-full">{date}</span>
                              </div>
                              {dateNotes.length > 0 && (
                                <span className="protocol-notes-date-count">
                                  {dateNotes.length}
                                </span>
                              )}
                            </button>
                            {/* Add note button in header */}
                            {isExpanded && editingDate !== date && (
                              <button
                                className="protocol-notes-add-note-header-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddNote(date);
                                }}
                                title="Add Note"
                              >
                                <Icon name="Plus" className="icon--sm" />
                              </button>
                            )}
                          </div>
                          
                          {isExpanded && (
                            <div className="protocol-notes-date-content">

                              {/* New note editor for this date */}
                              {editingDate === date && (
                                <div className="protocol-notes-note-item protocol-notes-note-item--new is-editing">
                                  <div className="protocol-notes-note-edit">
                                    <div className="protocol-notes-note-edit-header">
                                      <input
                                        type="date"
                                        value={editingDate}
                                        onChange={(e) => setEditingDate(e.target.value)}
                                        className="protocol-notes-note-date-input"
                                        max={new Date().toISOString().split('T')[0]}
                                      />
                                    </div>
                                    <input
                                      type="text"
                                      value={newNoteTitle}
                                      onChange={(e) => setNewNoteTitle(e.target.value)}
                                      className="protocol-notes-note-edit-title"
                                      placeholder="Title (optional)"
                                    />
                                    <textarea
                                      value={newNoteContent}
                                      onChange={(e) => setNewNoteContent(e.target.value)}
                                      className="protocol-notes-note-edit-textarea"
                                      placeholder="Write your note here..."
                                      rows={4}
                                      autoFocus={!newNoteTitle}
                                    />
                                    <div className="protocol-notes-note-edit-actions">
                                      <button
                                        className="protocol-notes-note-cancel-btn"
                                        onClick={handleCancelNewNote}
                                        disabled={saving}
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        className="protocol-notes-note-save-btn"
                                        onClick={handleSaveNewNote}
                                        disabled={!newNoteContent.trim() || saving}
                                      >
                                        {saving ? 'Saving...' : 'Save Note'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Existing notes for this date, grouped by time category */}
                              {(() => {
                                const notesByCategory = groupNotesByCategory(dateNotes);
                                const categoryOrder = ['morning', 'afternoon', 'evening'];
                                
                                return categoryOrder.map(category => {
                                  const categoryNotes = notesByCategory[category];
                                  if (categoryNotes.length === 0) return null;
                                  
                                  return (
                                    <div key={category} className="protocol-notes-category-group">
                                      <div className="protocol-notes-category-header">
                                        <span className="protocol-notes-category-name">
                                          {categoryNames[category]}
                                        </span>
                                      </div>
                                      <div className="protocol-notes-category-content">
                                        {categoryNotes.map(note => (
                                          <div key={note.id} className={`protocol-notes-note-item ${editingNoteId === note.id ? 'is-editing' : ''}`}>
                                  {editingNoteId === note.id ? (
                                    <div className="protocol-notes-note-edit">
                                      <div className="protocol-notes-note-edit-header">
                                        <input
                                          type="date"
                                          value={note.note_date}
                                          disabled
                                          className="protocol-notes-note-date-input"
                                        />
                                      </div>
                                      <input
                                        type="text"
                                        value={editingTitle}
                                        onChange={(e) => setEditingTitle(e.target.value)}
                                        className="protocol-notes-note-edit-title"
                                        placeholder="Title (optional)"
                                        autoFocus
                                      />
                                      <textarea
                                        value={editingContent}
                                        onChange={(e) => setEditingContent(e.target.value)}
                                        className="protocol-notes-note-edit-textarea"
                                        placeholder="Write your note here..."
                                        rows={4}
                                      />
                                      <div className="protocol-notes-note-edit-actions">
                                        <button
                                          className="protocol-notes-note-cancel-btn"
                                          onClick={handleCancelEdit}
                                          disabled={saving}
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          className="protocol-notes-note-save-btn"
                                          onClick={() => handleSaveInlineEdit(note.id)}
                                          disabled={!editingContent.trim() || saving}
                                        >
                                          {saving ? 'Saving...' : 'Save'}
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      {note.title && (
                                        <div className="protocol-notes-note-title">
                                          {note.title}
                                        </div>
                                      )}
                                      <div className="protocol-notes-note-content">
                                        {note.content}
                                      </div>
                                      <div className="protocol-notes-note-meta">
                                        <span className="protocol-notes-note-time">
                                          {new Date(note.updated_at).toLocaleTimeString('en-US', { 
                                            hour: 'numeric', 
                                            minute: '2-digit',
                                            hour12: true 
                                          })}
                                        </span>
                                        <div className="protocol-notes-note-actions">
                                          <button
                                            className="protocol-notes-note-action-btn"
                                            onClick={() => handleEditNote(note)}
                                            title="Edit note"
                                          >
                                            <Icon name="Edit" className="icon--sm" />
                                            Edit
                                          </button>
                                          <button
                                            className="protocol-notes-note-action-btn protocol-notes-note-action-btn--delete"
                                            onClick={() => handleDelete(note.id)}
                                            title="Delete note"
                                          >
                                            <Icon name="Trash2" className="icon--sm" />
                                            Delete
                                          </button>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="protocol-notes-empty">
                    <p>No notes yet. Add your first note using the Add Note button above.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProtocolNotesSlideOver;
